import { Box, Flex } from "@chakra-ui/react";
import colors from "core/resources/theme/colors";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import DefaultButton from "ui/components/Button/DefaultButton";
import Field from "ui/components/Input/Field";
import InputCep, { CepResponse } from "ui/components/Input/InputCep";
import SelectBairro from "ui/components/Input/SelectBairro";
import useGetBairros from "core/features/hooks/useGetBairros";

function DadosForm({
  onSubmit,
  data,
}: {
  onSubmit?: (data: any) => void;
  data: any;
}) {
  const { control, setValue, handleSubmit, reset, getValues, watch } = useForm<any>({});
  const [habilitaCampos, setHabilitarCampos] = useState<'possuiBairro' | 'naoPossuiBairro' | 'cadastrarBairro'>('possuiBairro');
  
  const cidadeId = watch('cidadeId');
  const bairroId = watch('bairroId');
  const { data: bairros } = useGetBairros(cidadeId);

  const afterLoadCep = (cepData: CepResponse) => {
    setValue("logradouro", cepData.logradouro);
    setValue("rua", cepData.logradouro);
    setValue("estado", cepData.ufSigla);
    setValue("ufSigla", cepData.ufSigla);
    setValue("nomeEstado", cepData.nomeEstado);
    setValue("cep", cepData.numero);
    setValue("cepInfo", cepData.numero);
    setValue("bairroId", cepData.bairroId);
    setValue("estadoId", cepData.ufSigla);
    setValue("cidadeId", cepData.cidadeId);
    setValue("cidadeNome", cepData.nomeCidade);
    setValue("bairroNome", cepData.nomeBairro);
    setValue("cidade", cepData.nomeCidade);
    setValue("uf", cepData.nomeEstado);
    setValue("nome", "Padrão");
    setValue("flgPrincipal", true);
    // Preserva número e complemento existentes
    const numeroAtual = getValues("numero");
    const complementoAtual = getValues("complemento");
    if (numeroAtual) setValue("numero", numeroAtual);
    if (complementoAtual) setValue("complemento", complementoAtual);
    
    if (cepData.habilitaCadastro) {
      setHabilitarCampos('naoPossuiBairro');
    }
  };

  // Atualiza bairroNome quando bairroId muda
  useEffect(() => {
    if (bairroId && bairros) {
      const bairroSelecionado = bairros.find((b: any) => b.id === bairroId);
      if (bairroSelecionado) {
        setValue("bairroNome", bairroSelecionado.nome);
      }
    }
  }, [bairroId, bairros, setValue]);

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      reset({
        ...data,
        estado: data.nomeEstado || data.uf,
        bairroNome: data.nomeBairro || data.bairroNome,
        cidade: data.nomeCidade || data.cidade,
        cepInfo: data.cep || data.cepInfo,
        flgPrincipal: true,
        nome: "Padrão"
      });
    }
  }, [data, reset]);

  useEffect(() => {
    if (habilitaCampos === 'cadastrarBairro') {
      setValue("bairroId", 0);
    }
  }, [setValue, habilitaCampos]);

  const handleFormSubmit = (formData: any) => {
    onSubmit?.(formData);
  };

  return (
    <Box as="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Flex flexDir="column" gap={4}>
        <Flex gap={6} flexDir="column">
          <Flex gap={6} flexDir={["column", "column", "row"]}>
            <Box w="100%">
              <Field
                control={control}
                name="cepInfo"
                inputProps={{ placeholder: "CEP*" }}
                rules={{
                  required: {
                    message: "Informe seu CEP",
                    value: true,
                  },
                }}
                render={({ field }) => (
                  <InputCep {...field} onLoadCep={afterLoadCep} />
                )}
              />
            </Box>
            <Box w="100%">
              <Field
                control={control}
                name="logradouro"
                inputProps={{ placeholder: "Rua*", disabled: habilitaCampos !== 'naoPossuiBairro' && habilitaCampos !== 'cadastrarBairro' }}
                rules={{
                  required: {
                    message: "Informe sua rua",
                    value: true,
                  },
                }}
              />
            </Box>
          </Flex>
          <Flex gap={6} flexDir={["column", "column", "row"]}>
            <Box w="100%">
              <Field
                control={control}
                name="numero"
                inputProps={{ placeholder: "Número*" }}
                rules={{
                  required: {
                    message: "Informe o número da sua residência",
                    value: true,
                  },
                }}
              />
            </Box>
            <Box w="100%">
              <Field
                control={control}
                name="complemento"
                inputProps={{ placeholder: "Complemento" }}
              />
            </Box>
            {habilitaCampos === 'possuiBairro' && (
              <Box w="100%">
                <Field
                  control={control}
                  name="bairroNome"
                  inputProps={{ placeholder: "Bairro*", disabled: true }}
                  rules={{
                    required: {
                      message: "Informe seu bairro",
                      value: true,
                    },
                  }}
                />
              </Box>
            )}
            {habilitaCampos === 'naoPossuiBairro' && (
              <Box w="100%">
                <Field
                  textBorder="Bairro"
                  control={control}
                  name="bairroId"
                  inputProps={{
                    placeholder: "Selecione*",
                  }}
                  rules={{
                    required: {
                      message: "Informe seu bairro",
                      value: true,
                    },
                  }}
                  render={({ field }) => <SelectBairro {...field} cidadeId={getValues('cidadeId')} />}
                />
              </Box>
            )}
            {habilitaCampos === 'cadastrarBairro' && (
              <Box w="100%">
                <Field
                  control={control}
                  name="bairroNome"
                  inputProps={{ placeholder: "Bairro*" }}
                  rules={{
                    required: {
                      message: "Informe seu bairro",
                      value: true,
                    },
                  }}
                />
              </Box>
            )}
          </Flex>
          <Flex gap={6} flexDir={["column", "column", "row"]}>
            <Box w="100%">
              <Field
                textBorder="Estado"
                control={control}
                name="estado"
                inputProps={{
                  placeholder: "Selecione*",
                  disabled: true,
                }}
                rules={{
                  required: {
                    message: "Informe seu estado",
                    value: true,
                  },
                }}
              />
            </Box>
            <Box w="100%">
              <Field
                textBorder="Cidade"
                control={control}
                name="cidade"
                inputProps={{
                  placeholder: "Selecione*",
                  disabled: true,
                }}
                rules={{
                  required: {
                    message: "Informe sua cidade",
                    value: true,
                  },
                }}
              />
            </Box>
          </Flex>
        </Flex>
      </Flex>

      <Flex flexDir="column" gap={4} mt={8} align="end">
        <DefaultButton 
          type="submit" 
          bg={colors.red} 
          title="SALVAR ENDEREÇO" 
        />
      </Flex>
    </Box>
  );
}

export default DadosForm;